import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import authApi from '../../services/api/auth';
import Icon from '../../components/AppIcon';
import AuthBrandPanel from '../../components/auth/AuthBrandPanel';
import rocketLogo from '../../assets/rocket-logo.png';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authApi.forgotPassword(email);
      setIsSubmitted(true);
    } catch (err) {
      // Show rate limit errors to the user
      if (err.status === 429) {
        setError(err.message || 'Too many requests. Please try again later.');
      } else {
        // For other errors, still show success to prevent email enumeration
        setIsSubmitted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet><title>Check Your Email - Rocket Tronc</title></Helmet>
    <div className="min-h-screen flex bg-[#f8f9fc]">
     <AuthBrandPanel />
   <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
   <div className="w-full max-w-md">
           <div className="lg:hidden text-center mb-8">
    <img src={rocketLogo} alt="Rocket" className="h-12 w-auto mx-auto mb-2" />
              </div>
     <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
      <div className="w-16 h-16 bg-[#00C9A7]/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Icon name="Mail" size={32} className="text-[#00C9A7]" />
        </div>
          <h1 className="text-2xl font-heading font-bold text-[#1a2b4b] mb-4">Check Your Email</h1>
                <p className="text-gray-500 mb-6">
       If an account exists with <strong className="text-[#1a2b4b]">{email}</strong>, we've sent a password reset link.
  The link will expire in 24 hours.
        </p>

        <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left border border-gray-100">
      <p className="text-sm font-semibold text-[#1a2b4b] mb-2">What to do next:</p>
   <ul className="text-sm text-gray-500 space-y-2">
        <li className="flex items-start gap-2">
     <Icon name="Mail" size={14} className="mt-1 text-[#FF5A5F] shrink-0" />
    Check your inbox for the password reset email
       </li>
     <li className="flex items-start gap-2">
     <Icon name="FolderArchive" size={14} className="mt-1 text-[#FF5A5F] shrink-0" />
  Check your spam or junk folder if you don't see it
                    </li>
      <li className="flex items-start gap-2">
      <Icon name="Link" size={14} className="mt-1 text-[#FF5A5F] shrink-0" />
 Click the link in the email to reset your password
        </li>
         </ul>
   </div>

       <div className="flex flex-col gap-3">
<button
          onClick={() => { setIsSubmitted(false); setEmail(''); }}
     className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-200 text-[#1a2b4b] font-semibold rounded-xl hover:bg-gray-50 transition-all"
           >
   <Icon name="RefreshCw" size={18} />
   Try Different Email
         </button>
     <Link to="/login" className="w-full flex items-center justify-center gap-2 py-3 px-4 text-gray-500 font-medium rounded-xl hover:bg-gray-50 transition-all">
           <Icon name="ArrowLeft" size={18} />
        Back to Login
       </Link>
           </div>
              </div>
 </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Forgot Password - Rocket Tronc</title></Helmet>
    <div className="min-h-screen flex bg-[#f8f9fc]">
        <AuthBrandPanel />
  <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-8">
              <img src={rocketLogo} alt="Rocket" className="h-12 w-auto mx-auto mb-2" />
        <p className="text-sm text-gray-500">TRONC Management</p>
            </div>

      <div className="text-center mb-8">
              <h2 className="text-3xl font-heading font-bold text-[#1a2b4b]">Forgot Password?</h2>
              <p className="text-gray-500 mt-2">Enter your email and we'll send you a reset link.</p>
    </div>

     <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
    {error && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 mb-6">
   <Icon name="AlertCircle" size={20} />
  <span className="text-sm font-medium">{error}</span>
      </div>
              )}

  <form onSubmit={handleSubmit} className="space-y-5">
   <div>
           <label htmlFor="email" className="block text-sm font-medium text-[#1a2b4b] mb-2">
        Email Address
        </label>
      <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
      <Icon name="Mail" size={18} className="text-gray-400" />
    </div>
             <input
       id="email"
           type="email"
value={email}
           onChange={(e) => setEmail(e.target.value)}
   className="w-full pl-11 pr-4 py-3 text-[#1a2b4b] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5A5F]/20 focus:border-[#FF5A5F] transition-all"
        placeholder="you@example.com"
            required
      autoFocus
    />
    </div>
    </div>

         <button
       type="submit"
 disabled={isLoading || !email}
   className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-[#FF5A5F] hover:bg-[#e54e53] text-white font-semibold rounded-xl shadow-lg shadow-[#FF5A5F]/25 hover:shadow-xl hover:shadow-[#FF5A5F]/30 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
  >
       {isLoading ? (
        <>
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
               Sending...
             </>
        ) : (
    <>
        <Icon name="Send" size={18} />
   Send Reset Link
    </>
       )}
  </button>
          </form>

    <div className="mt-6 text-center">
       <Link to="/login" className="text-sm text-[#FF5A5F] hover:text-[#e54e53] hover:underline flex items-center justify-center gap-2 transition-colors">
            <Icon name="ArrowLeft" size={16} />
              Back to Login
 </Link>
 </div>
  </div>

       <p className="text-center text-sm text-gray-400 mt-8 lg:hidden">
    &copy; {new Date().getFullYear()} Rocket. All rights reserved.
            </p>
          </div>
        </div>
  </div>
    </>
  );
};

export default ForgotPasswordPage;
